import { AgentRuleBundle } from "../agent-config-options";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentProcedure,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Sub-agent scaffolding for the docs-sync pipeline. Keeps documentation
 * in lockstep with code as the monorepo evolves by running in two
 * modes: (1) a **pre-PR sync check** that detects drift introduced by
 * a diff and updates docs or files follow-ups, and (2) a **full-
 * codebase audit** that produces a prioritized doc backlog.
 *
 * **Status: scaffolding only.** This bundle is the first child of the
 * docs-sync epic. The sub-agent prompt and skill instructions describe
 * the intended contract but the behavior is deliberately not
 * implemented — drift-detection, TSDoc stub insertion, API-extractor
 * integration, link integrity, and audit-mode scoring are all carried
 * by downstream child issues. Invoking `/docs-sync-pr` or
 * `/docs-sync-audit` against this scaffold produces a no-op session
 * that explains the state and stops.
 *
 * Implements the epic design in a neutral, domain-agnostic way:
 * configurable doc root (typically the Starlight singleton at
 * `/docs`) and configurable package roots (typically `packages/`)
 * come from the invoking issue body or the consuming project's
 * configuration. No hardcoded domains, companies, or product names.
 */
function buildDocsSyncSubAgent(_paths: ResolvedAgentPaths): AgentSubAgent {
  return {
    name: "docs-sync",
    description:
      "Keeps documentation in lockstep with code. Runs in two modes: (1) pre-PR sync check (diff-scoped, `docs-sync:scan` -> `docs-sync:fix`) that detects drift a diff introduces and either auto-applies mechanical fixes, flags advisory findings, or hard-blocks the PR; and (2) full-repo audit (`/docs-sync-audit`) that emits a prioritized `docs:write` backlog. This issue ships the scaffold only — behavior is implemented by downstream child issues.",
    model: AGENT_MODEL.BALANCED,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Docs-Sync Agent (scaffold)",
      "",
      "This sub-agent keeps documentation in lockstep with code. It runs",
      "in one of two modes per session:",
      "",
      "- **Pre-PR sync check** — invoked via `/docs-sync-pr`, scoped to",
      "  the diff on the current branch against `main`. Emits a sticky",
      "  `## Docs sync` comment on the PR, auto-applies mechanical fixes,",
      "  and either clears, adds an advisory, or applies",
      "  `review:human-required` to hand off to `pr-reviewer`.",
      "- **Full-repo audit** — invoked via `/docs-sync-audit`, walks the",
      "  whole monorepo (or a named scope), produces a prioritized",
      "  backlog of `docs:write` follow-up issues.",
      "",
      "## Status: scaffolding only",
      "",
      "This release ships the **skeleton** of the docs-sync pipeline so",
      "downstream child issues have a stable home. The behavior below is",
      "intentionally **not yet implemented**:",
      "",
      "- Drift detection (API-extractor, TSDoc parsing, reference",
      "  extraction, link integrity, code-sample compilation).",
      "- Mechanical fixes (TSDoc stubs, dead-link updates, registry",
      "  sync, rename-follow in prose).",
      "- Advisory / blocking routing, sticky PR comment, pr-reviewer",
      "  handoff.",
      "- Audit-mode walker, 5-factor scoring, `docs:write` backlog",
      "  emission.",
      "",
      "When invoked against this scaffold, post a one-line comment on",
      "the tracking issue explaining that the pipeline is not yet wired",
      "and stop. Do not attempt to apply fixes, open follow-up issues,",
      "or block a PR — the behavior belongs to subsequent child issues",
      "of the parent epic.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Overview",
      "",
      "The docs-sync pipeline mirrors the `maintenance-audit` pattern:",
      "audit-first, narrow-write second, filesystem-durable between",
      "phases. It runs through **2 phases** once the pipeline is wired:",
      "",
      "| Phase | Label | Writes? |",
      "|-------|-------|---------|",
      "| 1. Scan | `docs-sync:scan` | Audit report to disk; no doc edits |",
      "| 2. Fix | `docs-sync:fix` | **Narrow** mechanical edits only, plus `docs:write` follow-ups for conceptual changes |",
      "",
      "Drift classifications are encoded via two additional labels:",
      "",
      "| Label | Meaning |",
      "|-------|---------|",
      "| `docs-sync:advisory` | Drift was detected but does not block the PR. Posted as a non-blocking note on the sticky `## Docs sync` comment. |",
      "| `docs-sync:blocking` | Drift blocks the PR. Applied together with `review:human-required` so `pr-reviewer` owns the merge gate. |",
      "",
      "All pipeline issues also carry `type:docs-sync` and a `status:*`",
      "label.",
      "",
      "## Invocation modes (scaffolded)",
      "",
      "- **`/docs-sync-pr <branch>`** — diff-scoped check against",
      "  `main`. Intended to run after a code change, before",
      "  `/review-pr`. Manual invocation only in this scaffolding",
      "  release; a GitHub Actions trigger is on the roadmap (tracked",
      "  as a follow-up epic to the parent docs-sync epic).",
      "- **`/docs-sync-audit [<scope>]`** — full-repo or scoped audit.",
      "  Produces a prioritized backlog of `docs:write` issues once",
      "  child issues land the walker and the scoring model.",
      "",
      "---",
      "",
      "## Phase 1: Scan",
      "",
      "Phase 1 walks the repo, runs every registered drift check, and",
      "writes a single combined **audit report** to disk. Phase 1 never",
      "edits documentation — only the persisted report changes between",
      "scan and fix.",
      "",
      "### Audit report",
      "",
      "The report shape is exported from `@codedrifters/configulator`",
      "as `AuditReport` (under `src/docs-sync/scan/`) so Phase 2 and",
      "any downstream consumers share one definition. Reports are",
      "persisted at:",
      "",
      "```",
      ".state/docs-sync/<issue-number>-audit.json",
      "```",
      "",
      "Every report carries a `schemaVersion`, the `issueNumber`, the",
      "invocation `mode` (`pr` or `audit`), the `generatedAt` UTC",
      "timestamp, the `repoRoot`, and an optional `scope` label, plus",
      "two parallel views of the findings:",
      "",
      "- **`categories`** — findings grouped by drift-check category.",
      "  Every category key is always present; empty arrays are",
      "  persisted explicitly. The five categories are `apiDiff`,",
      "  `tsdocCoverage`, `referenceMismatches`, `linkFailures`, and",
      "  `sampleFailures`.",
      "- **`findings`** — the same records re-aggregated as a single",
      "  flat array, sorted by severity (`blocking` first), then",
      "  category, file, line, and subject. Provided so consumers",
      "  that don't need the category grouping can iterate one array",
      "  and see the merge-blocking findings at the top.",
      "",
      "Every finding carries a `category`, `severity`",
      "(`mechanical` / `advisory` / `blocking`), `location`, `subject`,",
      "`details`, and an optional `fixHint` for the Phase 2 fix agent.",
      "",
      "### Orchestrator",
      "",
      "The scan is driven by `runScan(...)` (also exported under",
      "`src/docs-sync/scan/`). The orchestrator accepts a list of",
      "`AuditCheckRunner` instances, invokes each one in registration",
      "order, merges the findings into the report, and (optionally)",
      "persists the report to disk. The default check list is empty —",
      "this skeleton release runs cleanly with no checks registered",
      "and produces a schema-compliant report whose category arrays",
      "are all empty.",
      "",
      "Children of the parent docs-sync epic plug runners into the",
      "orchestrator: API/TSDoc/reference checks land first, link",
      "and fenced-sample checks land second, the fix phase reads the",
      "persisted report, and the audit-mode walker reuses the same",
      "report shape.",
      "",
      "---",
      "",
      "## Write Boundaries",
      "",
      "Even once the pipeline is wired, the following boundaries are",
      "non-negotiable:",
      "",
      "- **Mechanical fixes only.** The agent never rewrites conceptual",
      "  prose, ADRs, requirement bodies, or BCM capability models.",
      "  Those go to `docs:write` follow-ups routed to the appropriate",
      "  writer bundle or to a human.",
      "- **Policy-protected paths stay off-limits.** Paths already",
      "  listed in `human-required.paths` in the PR Review Policy",
      "  (ADRs, `project-context.md`, `.github/workflows/**`,",
      "  `.projenrc.ts`, `CLAUDE.md`, `.claude/**`,",
      "  `packages/**/package.json`) are never auto-edited by this",
      "  agent — it only flags them.",
      "- **No merge calls.** The agent never invokes `gh pr merge`.",
      "  When drift blocks a PR it applies `review:human-required`",
      "  and `pr-reviewer` owns the merge gate.",
      "- **AST-grounded references.** All symbol references are",
      "  extracted from the TypeScript compiler AST, never from prose",
      "  recall — preventing hallucinated API names.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **Scaffolding only.** Do not implement drift detection,",
      "  mechanical fixes, or backlog emission in this sub-agent prompt —",
      "  those behaviors belong to downstream child issues of the parent",
      "  epic.",
      "- **Stop on invocation.** When invoked against the scaffold,",
      "  post a one-line comment explaining the pipeline is not yet",
      "  wired and stop.",
      "- **Respect the two-phase contract.** Once children land the",
      "  behavior, Phase 1 (Scan) writes a durable audit report and",
      "  never edits docs; Phase 2 (Fix) reads the report, applies only",
      "  mechanical idempotent fixes, and files `docs:write` follow-ups",
      "  for conceptual changes.",
    ].join("\n"),
  };
}

/**
 * Skill stub: pre-PR drift check. Invocation currently no-ops against
 * the scaffold — behavior is carried by downstream child issues of
 * the parent docs-sync epic.
 */
function buildDocsSyncPrSkill(): AgentSkill {
  return {
    name: "docs-sync-pr",
    description:
      "Pre-PR docs-sync drift check (scaffold). Once downstream child issues wire the behavior, this skill runs a diff-scoped scan against main, auto-applies mechanical fixes, and either clears, posts an advisory, or applies `review:human-required`. Currently no-ops with an explanatory comment.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "docs-sync",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Docs-Sync PR (scaffold)",
      "",
      "Run the pre-PR docs-sync drift check against the current branch.",
      "",
      "## Status: scaffold",
      "",
      "This skill is part of the docs-sync scaffolding release. The",
      "drift-detection behavior is deliberately not yet implemented —",
      "downstream child issues of the parent docs-sync epic will wire",
      "API-extractor diffing, TSDoc parsing, reference extraction, link",
      "integrity, and code-sample compilation into a single scan, then",
      "add the fix phase that auto-applies mechanical repairs or flags",
      "advisory / blocking drift.",
      "",
      "When invoked today, this skill should:",
      "",
      "1. Post a one-line comment on the invoking PR explaining that",
      "   the docs-sync pipeline is scaffolded but not yet functional.",
      "2. Exit without creating issues, editing files, or applying",
      "   labels.",
      "",
      "## Usage (once wired)",
      "",
      "```",
      "/docs-sync-pr <branch>",
      "```",
      "",
      "Where `<branch>` is the head branch of the PR to check. The",
      "skill diffs the branch against `main`, runs the scan checks, and",
      "posts a sticky `## Docs sync` comment on the PR.",
      "",
      "## Output (once wired)",
      "",
      "- Mechanical fixes committed to the branch under narrow write",
      "  boundaries (missing TSDoc stubs, dead-link updates, registry",
      "  sync, rename-follow in prose).",
      "- A sticky `## Docs sync` comment summarizing detected drift,",
      "  advisory findings, and any blocking issues.",
      "- `docs-sync:advisory` or `docs-sync:blocking` applied to the",
      "  PR as appropriate. Blocking drift additionally applies",
      "  `review:human-required` so `pr-reviewer` owns the merge gate.",
    ].join("\n"),
  };
}

/**
 * Skill stub: full-repo docs audit. Invocation currently no-ops
 * against the scaffold — behavior is carried by downstream child
 * issues of the parent docs-sync epic.
 */
function buildDocsSyncAuditSkill(): AgentSkill {
  return {
    name: "docs-sync-audit",
    description:
      "Full-repo docs-sync audit (scaffold). Once downstream child issues wire the walker and the 5-factor scoring model, this skill emits a prioritized `docs:write` backlog. Currently no-ops with an explanatory comment.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "docs-sync",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Docs-Sync Audit (scaffold)",
      "",
      "Run the full-repo (or scoped) docs-sync audit. Produces a",
      "prioritized backlog of `docs:write` follow-up issues ordered by",
      "API-surface coverage, critical-path weight, stale-page age,",
      "orphan-page detection, and inbound link count.",
      "",
      "## Status: scaffold",
      "",
      "This skill is part of the docs-sync scaffolding release. The",
      "audit walker and scoring model are deliberately not yet",
      "implemented — downstream child issues of the parent docs-sync",
      "epic will wire them in.",
      "",
      "When invoked today, this skill should:",
      "",
      "1. Post a one-line comment on the invoking issue explaining",
      "   that the audit walker is scaffolded but not yet functional.",
      "2. Exit without creating issues, editing files, or applying",
      "   labels.",
      "",
      "## Usage (once wired)",
      "",
      "```",
      "/docs-sync-audit [<scope>]",
      "```",
      "",
      "Where `<scope>` is an optional path filter (for example a",
      "specific package or a specific docs subtree). Omit the scope",
      "to audit the full repo.",
      "",
      "## Output (once wired)",
      "",
      "- A `docs-sync-audit-<YYYY-MM-DD>.md` report committed to the",
      "  docs audit root.",
      "- One `docs:write` follow-up issue per actionable finding,",
      "  prioritized per the 5-factor scoring model declared in the",
      "  parent epic's design.",
    ].join("\n"),
  };
}

/**
 * Render the shell body of the `.claude/procedures/extract-api.sh`
 * helper. Exported so the docs-sync bundle can register it as an
 * `AgentProcedure` and the bundles test suite can assert on the
 * script's contents.
 *
 * The helper runs `@microsoft/api-extractor` end-to-end for a single
 * package and writes the `.api.md` rollup to the scratch folder
 * declared by that package's `api-extractor.json`. Rollups are
 * **regenerate-on-scan** per the docs-sync epic resolved decision #3
 * — the scan phase consumes the freshly-regenerated rollup in-memory
 * rather than comparing against a committed baseline.
 *
 * Exit codes: `0` success, `1` usage / missing directory, `2` no
 * `api-extractor.json` at the target path, `3` the extractor exited
 * non-zero (compile error or extractor failure).
 */
export function renderExtractApiProcedure(): string {
  return [
    "#!/usr/bin/env bash",
    "# extract-api.sh — Run @microsoft/api-extractor for a single package.",
    "#",
    "# Usage:",
    "#   .claude/procedures/extract-api.sh <package-dir>",
    "#",
    "# Where `<package-dir>` is a path (relative to the repo root, or",
    "# absolute) to a sub-project that carries an `api-extractor.json`",
    "# file generated by the configulator `ApiExtractor` component.",
    "#",
    "# Runs the extractor end-to-end and writes the `.api.md` rollup to",
    "# the scratch folder declared by the package's `api-extractor.json`",
    "# (default: `<package-dir>/.api-extractor/`). Rollups are",
    "# **regenerated on scan** rather than committed to git per the",
    "# docs-sync epic resolved decision #3 — the scan phase consumes the",
    "# freshly-regenerated rollup in-memory.",
    "#",
    "# Exit codes:",
    "#   0  — extraction succeeded; rollup written to the scratch folder.",
    "#   1  — usage error (missing / unreadable package dir).",
    "#   2  — the package has no `api-extractor.json` config.",
    "#   3  — the extractor exited non-zero (compile error, missing entry",
    "#        point, extractor-internal failure).",
    "#",
    "# Dependencies: expects `pnpm` on PATH and the target package's",
    "# `node_modules` to carry `@microsoft/api-extractor` (added as a",
    "# devDep by configulator's TypeScriptProject class).",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "extract-api.sh: %s\\n" "$*" >&2',
    "}",
    "",
    'if [ "$#" -lt 1 ]; then',
    '  err "usage: extract-api.sh <package-dir>"',
    "  exit 1",
    "fi",
    "",
    'pkg_dir="$1"',
    "",
    'if [ ! -d "$pkg_dir" ]; then',
    '  err "package directory not found: $pkg_dir"',
    "  exit 1",
    "fi",
    "",
    'config_file="$pkg_dir/api-extractor.json"',
    'if [ ! -f "$config_file" ]; then',
    '  err "no api-extractor.json found at $config_file"',
    "  err \"ensure the package is a TypeScriptProject with ApiExtractor enabled and re-run 'pnpm exec projen'.\"",
    "  exit 2",
    "fi",
    "",
    "# Resolve the binary. Prefer the target package's local binary so",
    "# the extractor version matches what configulator pinned for this",
    "# package. Fall back to a workspace-aware `pnpm exec` lookup when",
    "# the package hasn't installed its own copy yet. The local-bin",
    "# path is resolved relative to the package root so the lookup",
    "# still works after we `cd` into it.",
    'local_bin="./node_modules/.bin/api-extractor"',
    'if [ -x "$pkg_dir/node_modules/.bin/api-extractor" ]; then',
    '  bin_cmd=("$local_bin")',
    "else",
    '  bin_cmd=(pnpm --filter "$(node -p "require(\'$pkg_dir/package.json\').name")" exec api-extractor)',
    "fi",
    "",
    "# `--local` tells the extractor to regenerate the report in place",
    "# rather than treating a missing file as a baseline-diff violation.",
    "# The scan phase captures its own baseline in-memory — the file on",
    "# disk is just the currently-extracted rollup.",
    "#",
    "# `@microsoft/api-extractor` does not create the report folder",
    "# itself; ensure it exists so the first run after a fresh clone",
    "# does not fail on a missing scratch directory.",
    "(",
    '  cd "$pkg_dir" && mkdir -p .api-extractor && "${bin_cmd[@]}" run --local --config api-extractor.json',
    ")",
    "status=$?",
    "",
    'if [ "$status" -ne 0 ]; then',
    '  err "api-extractor exited with status $status for $pkg_dir"',
    "  exit 3",
    "fi",
    "",
    "# Surface the path to the produced rollup so callers can read it",
    "# without hard-coding the scratch folder layout. The extractor",
    "# writes `<unscopedPackageName>.api.md` into the folder declared",
    "# by `apiReport.reportFolder` in `api-extractor.json` — matches",
    "# the default the configulator ApiExtractor component ships.",
    'report_dir="$pkg_dir/.api-extractor"',
    'if [ -d "$report_dir" ]; then',
    '  find "$report_dir" -maxdepth 2 -type f -name "*.api.md" -print || true',
    "fi",
    "",
    "exit 0",
  ].join("\n");
}

/**
 * `AgentProcedure` definition for `.claude/procedures/extract-api.sh`.
 * Registered on the docs-sync bundle so it ships when the bundle is
 * force-included — matches the packaging of other bundled procedures
 * (see `orchestratorBundle.procedures`).
 */
export const extractApiProcedure: AgentProcedure = {
  name: "extract-api.sh",
  description:
    "Run @microsoft/api-extractor for a single package and write the .api.md rollup to the scratch folder declared by the package's api-extractor.json. Rollups are regenerate-on-scan per docs-sync epic #448 resolved decision #3 — never committed to git.",
  content: renderExtractApiProcedure(),
};

/**
 * Render the shell body of the `.claude/procedures/check-links.sh`
 * helper. Exported so the docs-sync bundle can register it as an
 * `AgentProcedure` and the bundles test suite can assert on the
 * script's contents.
 *
 * The helper wraps two external tools — `astro check` (internal
 * links) and `lychee` (external `https://…` URLs) — and normalizes
 * their per-finding output into a single JSON-array stream of
 * `{ url, docPath, line, kind, reason }` records on stdout. The
 * downstream docs-sync scan phase (#519/#520) consumes that stream
 * and decides which findings are advisory and which block the PR.
 *
 * Detection is **data**, not failure: the helper exits `0` whenever
 * a tool ran successfully, regardless of how many broken links it
 * reported. Non-zero exits are reserved for tool-level failures
 * (missing binary, config error, IO failure).
 *
 * Exit codes: `0` success, `1` usage error or unreadable docs
 * root, `2` a required external tool is missing, `3` a tool ran
 * but exited non-zero for a reason other than broken-link
 * detection.
 */
export function renderCheckLinksProcedure(): string {
  return [
    "#!/usr/bin/env bash",
    "# check-links.sh — Link integrity checker for the Starlight docs site.",
    "#",
    "# Usage:",
    "#   .claude/procedures/check-links.sh <mode> <docs-root> [<site-dir>]",
    "#",
    "# Where:",
    "#   <mode>      — one of `internal`, `external`, or `all`.",
    "#   <docs-root> — path to the markdown content tree",
    "#                 (e.g. `docs/src/content/docs`). Walked recursively",
    "#                 for `*.md` / `*.mdx` files.",
    "#   <site-dir>  — (optional, internal/all only) path to the Starlight",
    "#                 project root that contains `astro.config.{ts,mjs,js}`.",
    "#                 Defaults to two levels up from `<docs-root>` so the",
    "#                 standard Starlight layout (`<site>/src/content/docs`)",
    "#                 resolves automatically.",
    "#",
    "# Wraps two external tools:",
    "#   - `astro check`  — internal links and content-collection refs",
    "#                      inside the Starlight site.",
    "#   - `lychee`       — external `https://…` URLs across the docs tree",
    "#                      (https://github.com/lycheeverse/lychee).",
    "#",
    "# Output (stdout, single JSON array):",
    "#",
    "#   [",
    "#     {",
    '#       "url":     "<href as written in the markdown source>",',
    '#       "docPath": "<path relative to <docs-root>>",',
    '#       "line":    <1-indexed line number>,',
    '#       "kind":    "internal" | "external",',
    '#       "reason":  "<short tool-supplied diagnostic>"',
    "#     },",
    "#     ...",
    "#   ]",
    "#",
    "# An empty array (`[]`) is emitted when no broken links are found.",
    "# Detection is **data**, not failure: this helper exits 0 when a",
    "# tool ran successfully and reported zero or more broken links.",
    "# The downstream docs-sync scan phase (#519/#520) is responsible",
    "# for deciding which findings are advisory and which block.",
    "#",
    "# Exit codes:",
    "#   0  — every requested tool ran successfully; findings (zero or",
    "#        more) are on stdout as a JSON array.",
    "#   1  — usage error (missing args, unreadable docs root).",
    "#   2  — a required external tool is not installed or not on PATH",
    "#        (`astro` / `lychee` / `node` / `jq`).",
    "#   3  — a tool ran but exited non-zero for a reason other than",
    "#        broken-link detection (config error, IO error, network",
    "#        failure for lychee, etc.). Stderr carries the diagnostic.",
    "#",
    "# The script never edits files. Diagnostics flow to stderr; the",
    "# only thing on stdout is the normalized JSON array.",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "check-links.sh: %s\\n" "$*" >&2',
    "}",
    "",
    'if [ "$#" -lt 2 ]; then',
    '  err "usage: check-links.sh <internal|external|all> <docs-root> [<site-dir>]"',
    "  exit 1",
    "fi",
    "",
    'mode="$1"',
    'docs_root="$2"',
    'site_dir="${3:-}"',
    "",
    'case "$mode" in',
    "  internal | external | all) ;;",
    "  *)",
    "    err \"unknown mode '$mode' — expected 'internal', 'external', or 'all'\"",
    "    exit 1",
    "    ;;",
    "esac",
    "",
    'if [ ! -d "$docs_root" ]; then',
    '  err "docs root not found: $docs_root"',
    "  exit 1",
    "fi",
    "",
    'if [ -z "$site_dir" ]; then',
    "  # Standard Starlight layout: <site>/src/content/docs. Walk two",
    "  # parents up from the content root to recover the site dir.",
    '  site_dir="$(cd "$docs_root/../../.." && pwd)"',
    "fi",
    "",
    "# `jq` is required to merge the per-tool JSON output streams. Fail",
    "# fast with the dedicated tool-missing exit code if it is absent.",
    "if ! command -v jq >/dev/null 2>&1; then",
    '  err "jq is required but not on PATH"',
    "  exit 2",
    "fi",
    "",
    'tmp_dir="$(mktemp -d -t check-links-XXXXXX)"',
    "# shellcheck disable=SC2064",
    "trap \"rm -rf '$tmp_dir'\" EXIT",
    "",
    'internal_json="$tmp_dir/internal.json"',
    'external_json="$tmp_dir/external.json"',
    'printf "[]" > "$internal_json"',
    'printf "[]" > "$external_json"',
    "",
    "# ---------------------------------------------------------------",
    "# Internal-link check",
    "# ---------------------------------------------------------------",
    "# `astro check` runs inside the Starlight project root and walks",
    "# content collections to validate internal references. It exits",
    "# non-zero when references are broken — that is a *finding* for",
    "# this helper, not a tool failure, so we capture stderr and parse",
    "# it into the normalized record shape rather than propagating the",
    "# non-zero exit.",
    'if [ "$mode" = "internal" ] || [ "$mode" = "all" ]; then',
    "  if ! command -v node >/dev/null 2>&1; then",
    '    err "node is required but not on PATH"',
    "    exit 2",
    "  fi",
    "",
    '  if [ ! -d "$site_dir" ]; then',
    '    err "site dir not found: $site_dir"',
    "    exit 1",
    "  fi",
    "",
    "  # Locate the astro binary in the site's local node_modules first",
    "  # so the version matches what `pnpm install` resolved for the",
    "  # Starlight site. Fall back to a workspace-aware `pnpm exec`.",
    '  if [ -x "$site_dir/node_modules/.bin/astro" ]; then',
    '    astro_bin=("$site_dir/node_modules/.bin/astro")',
    "  elif command -v pnpm >/dev/null 2>&1; then",
    '    astro_bin=(pnpm --dir "$site_dir" exec astro)',
    "  else",
    '    err "astro binary not found in $site_dir/node_modules/.bin and pnpm is unavailable"',
    "    exit 2",
    "  fi",
    "",
    '  astro_log="$tmp_dir/astro.log"',
    "  set +e",
    '  ( cd "$site_dir" && "${astro_bin[@]}" check ) > "$astro_log" 2>&1',
    "  astro_status=$?",
    "  set -e",
    "",
    "  # `astro check` exits 1 on findings and >=2 on tool failure.",
    "  # Translate to the normalized record stream regardless of exit.",
    '  if [ "$astro_status" -ge 2 ]; then',
    '    err "astro check exited with status $astro_status (tool failure)"',
    '    cat "$astro_log" >&2 || true',
    "    exit 3",
    "  fi",
    "",
    "  # Parse the line-oriented log into JSON records. The `astro",
    "  # check` output for a broken reference looks like:",
    "  #",
    "  #   src/content/docs/foo.md:42:8 - error <code>: <reason>",
    "  #",
    "  # The helper extracts `<docPath>`, `<line>`, and `<reason>` and",
    "  # synthesises a `url` field by quoting the offending excerpt",
    "  # when astro reports one (it does not always); when no excerpt",
    "  # is available we fall back to an empty string so the schema",
    "  # stays uniform.",
    '  awk -v docs_root="$docs_root" -v site_dir="$site_dir" \'',
    "    BEGIN {",
    '      printf "[";',
    "      first = 1;",
    "    }",
    '    # Match "<path>:<line>:<col> - <severity> <code>: <message>"',
    "    /^.+:[0-9]+:[0-9]+ - / {",
    '      split($0, head, " - ");',
    '      n = split(head[1], loc, ":");',
    "      file = loc[1];",
    '      for (i = 2; i < n - 1; i++) { file = file ":" loc[i]; }',
    "      line = loc[n - 1];",
    '      message = "";',
    "      for (i = 2; i <= length(head); i++) {",
    '        if (i > 2) message = message " - ";',
    "        message = message head[i];",
    "      }",
    "      # Resolve the file relative to docs_root for stability.",
    "      abs = file;",
    '      if (substr(abs, 1, 1) != "/") {',
    '        abs = site_dir "/" abs;',
    "      }",
    '      sub(docs_root "/", "", abs);',
    '      gsub(/"/, "\\\\\\"", message);',
    '      gsub(/"/, "\\\\\\"", abs);',
    '      if (!first) printf ",";',
    "      first = 0;",
    '      printf "{\\"url\\":\\"\\",\\"docPath\\":\\"%s\\",\\"line\\":%s,\\"kind\\":\\"internal\\",\\"reason\\":\\"%s\\"}", abs, line, message;',
    "    }",
    "    END {",
    '      printf "]";',
    "    }",
    '  \' "$astro_log" > "$internal_json"',
    "",
    "  # Validate the JSON we synthesised. Catches awk escaping bugs",
    "  # before we hand the payload to the merge step.",
    '  if ! jq -e . "$internal_json" >/dev/null 2>&1; then',
    '    err "failed to normalize astro check output to JSON"',
    '    cat "$internal_json" >&2 || true',
    "    exit 3",
    "  fi",
    "fi",
    "",
    "# ---------------------------------------------------------------",
    "# External-link check",
    "# ---------------------------------------------------------------",
    "# `lychee` walks the markdown tree and reports unreachable URLs.",
    "# Its `--format json` output is structured per-link; we project",
    "# only the failed entries into the normalized record shape.",
    'if [ "$mode" = "external" ] || [ "$mode" = "all" ]; then',
    "  if ! command -v lychee >/dev/null 2>&1; then",
    '    err "lychee is required but not on PATH (https://github.com/lycheeverse/lychee)"',
    "    exit 2",
    "  fi",
    "",
    '  lychee_json="$tmp_dir/lychee.json"',
    "  set +e",
    "  # `--no-progress` avoids interactive carriage-returns mangling",
    "  # the JSON. `--format json` emits a single object whose `fail_map`",
    "  # field lists the broken URLs grouped by source file.",
    "  # `--exclude-mail` skips mailto: links (out of scope for this",
    "  # helper).",
    '  lychee --no-progress --format json --exclude-mail "$docs_root" > "$lychee_json" 2>"$tmp_dir/lychee.err"',
    "  lychee_status=$?",
    "  set -e",
    "",
    "  # lychee exits 0 when every link resolved, 2 when one or more",
    "  # links failed. Anything else is a tool failure (config, IO).",
    '  if [ "$lychee_status" -ne 0 ] && [ "$lychee_status" -ne 2 ]; then',
    '    err "lychee exited with status $lychee_status (tool failure)"',
    '    cat "$tmp_dir/lychee.err" >&2 || true',
    "    exit 3",
    "  fi",
    "",
    "  # Project lychee's `fail_map` into the normalized record shape.",
    '  # The `fail_map` is `{ "<source-path>": [ { url, status: { details? } }, ... ] }`.',
    "  # lychee does not report a line number for every match, so we",
    "  # default to 0 when it is missing (downstream consumers treat",
    '  # 0 as "unknown line").',
    '  jq --arg root "$docs_root" \'',
    "    def trim_root($p; $r):",
    '      if ($p | startswith($r + "/")) then ($p | ltrimstr($r + "/"))',
    '      elif ($p == $r) then ""',
    "      else $p end;",
    "    [",
    "      ( .fail_map // {} ) as $map",
    "      | $map | to_entries[]",
    "      | .key as $src",
    "      | .value[]",
    "      | {",
    '          url: (.url // ""),',
    "          docPath: trim_root($src; $root),",
    "          line: ((.line // 0) | tonumber? // 0),",
    '          kind: "external",',
    '          reason: ( (.status.details // .status.text // .status.code // "broken") | tostring )',
    "        }",
    "    ]",
    '  \' "$lychee_json" > "$external_json" 2>"$tmp_dir/lychee.parse.err"',
    "",
    '  if ! jq -e . "$external_json" >/dev/null 2>&1; then',
    '    err "failed to parse lychee JSON output"',
    '    cat "$tmp_dir/lychee.parse.err" >&2 || true',
    '    cat "$lychee_json" >&2 || true',
    "    exit 3",
    "  fi",
    "fi",
    "",
    "# ---------------------------------------------------------------",
    "# Merge & emit",
    "# ---------------------------------------------------------------",
    "# Sort records deterministically (kind, docPath, line, url) so",
    "# downstream consumers can compare reports across runs without",
    "# fighting platform-specific ordering noise.",
    "jq -s '",
    "  add",
    "  | sort_by(.kind, .docPath, .line, .url)",
    '\' "$internal_json" "$external_json"',
    "",
    "exit 0",
  ].join("\n");
}

/**
 * `AgentProcedure` definition for `.claude/procedures/check-links.sh`.
 * Registered on the docs-sync bundle so it ships when the bundle is
 * force-included — matches the packaging of `extractApiProcedure`
 * above. Provides the link-integrity input the docs-sync scan phase
 * (#519/#520) consumes alongside API-extractor and TSDoc-coverage
 * findings.
 */
export const checkLinksProcedure: AgentProcedure = {
  name: "check-links.sh",
  description:
    "Link integrity checker that wraps `astro check` (internal links) and `lychee` (external URLs) and normalizes their output into a single JSON-array stream of { url, docPath, line, kind, reason } records. Detection is data: the helper exits 0 when a tool ran successfully regardless of how many broken links it reported. Non-zero exits are reserved for tool-level failures.",
  content: renderCheckLinksProcedure(),
};

/**
 * Render the shell body of the
 * `.claude/procedures/strip-tool-artifact-tags.sh` helper. Exported so
 * the docs-sync bundle can register it as an `AgentProcedure` and the
 * bundles test suite can assert on the script's contents.
 *
 * Authoring agents intermittently leak tool-call wrapper *closing*
 * tags (`</content>`, `</invoke>`, occasionally `</parameter>`) as
 * trailing whole lines in the markdown they write. `astro check` and
 * CI link checks do not catch them. This helper strips those leaked
 * EOF artifact lines on write so they never reach the committed tree.
 *
 * Behaviour, mirroring `check-links.sh`'s defensive guards:
 *
 *   - Takes a single file-path argument.
 *   - Operates only when the file exists and its path is under
 *     `docs/src/content/docs/`. Any other path (or a missing file) is
 *     a silent no-op.
 *   - Removes **trailing whole lines** that are exactly `</content>`,
 *     `</invoke>`, or `</parameter>` (trailing whitespace on the line
 *     is tolerated), plus any blank lines that become trailing once
 *     the tags are removed, then leaves a single final newline.
 *   - Only whole-line EOF tags are stripped — legitimate inline
 *     `<...>` prose or fenced code is never touched.
 *   - Idempotent: a second run on an already-clean file changes
 *     nothing.
 *   - Never edits a file it did not need to change, and **always**
 *     exits 0 so a PostToolUse hook can never fail the tool call.
 *
 * Exit code: always `0`. Diagnostics (if any) flow to stderr.
 */
export function renderStripToolArtifactTagsProcedure(): string {
  return [
    "#!/usr/bin/env bash",
    "# strip-tool-artifact-tags.sh — remove leaked tool-call wrapper",
    "# closing tags from the end of an authored markdown file.",
    "#",
    "# Usage:",
    "#   .claude/procedures/strip-tool-artifact-tags.sh <file-path>",
    "#",
    "# Authoring agents occasionally leak tool-call wrapper *closing*",
    "# tags (</content>, </invoke>, </parameter>) as trailing whole",
    "# lines in the markdown they write. astro check and CI link checks",
    "# do not catch them. This helper strips those EOF artifact lines",
    "# (plus any now-trailing blank lines) and rewrites the file with a",
    "# single final newline.",
    "#",
    "# Guards (mirrors check-links.sh):",
    "#   - Operates only on an existing file whose path is under",
    "#     docs/src/content/docs/. Any other path, or a missing file,",
    "#     is a silent no-op.",
    "#   - Only WHOLE-LINE EOF tags are removed; inline `<...>` prose or",
    "#     fenced code is never touched.",
    "#   - Idempotent: a re-run on a clean file changes nothing.",
    "#   - Never fails the tool call — always exits 0. Diagnostics go to",
    "#     stderr only; the file is rewritten only when it changes.",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "strip-tool-artifact-tags.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "# No file argument: nothing to do.",
    'if [ "$#" -lt 1 ]; then',
    "  exit 0",
    "fi",
    "",
    'file="$1"',
    "",
    "# No-op unless the file actually exists and is a regular file.",
    'if [ ! -f "$file" ]; then',
    "  exit 0",
    "fi",
    "",
    "# Path guard: only touch markdown under the Starlight content tree.",
    "# Match the canonical content-root segment anywhere in the path so",
    "# the guard works for both absolute and repo-relative paths.",
    'case "$file" in',
    "  *docs/src/content/docs/*) ;;",
    "  *) exit 0 ;;",
    "esac",
    "",
    "# Rewrite the file with trailing artifact tags and trailing blank",
    "# lines removed. awk buffers every line, then walks back from EOF",
    "# dropping lines that are blank or exactly one of the leaked",
    "# closing tags (trailing whitespace tolerated). Whatever survives",
    "# is re-emitted with a single final newline. Only whole lines are",
    "# considered, so inline `<...>` content is never altered.",
    'tmp_out="$(mktemp -t strip-tool-artifact-tags-XXXXXX)" || exit 0',
    "# shellcheck disable=SC2064",
    "trap \"rm -f '$tmp_out'\" EXIT",
    "",
    "awk '",
    "  { lines[NR] = $0 }",
    "  END {",
    "    last = NR",
    "    while (last > 0) {",
    "      line = lines[last]",
    "      # Strip trailing whitespace (spaces, tabs, CR) for the match.",
    "      stripped = line",
    '      sub(/[ \\t\\r]+$/, "", stripped)',
    '      if (stripped == "" \\',
    '          || stripped == "</content>" \\',
    '          || stripped == "</invoke>" \\',
    '          || stripped == "</parameter>") {',
    "        last--",
    "        continue",
    "      }",
    "      break",
    "    }",
    "    for (i = 1; i <= last; i++) {",
    "      print lines[i]",
    "    }",
    "  }",
    '\' "$file" > "$tmp_out" || exit 0',
    "",
    "# Only write back when the content actually changed — keeps the",
    "# helper a true no-op on clean files (idempotent re-runs included).",
    'if ! cmp -s "$tmp_out" "$file"; then',
    '  if ! cat "$tmp_out" > "$file"; then',
    '    err "failed to rewrite $file"',
    "  fi",
    "fi",
    "",
    "exit 0",
  ].join("\n");
}

/**
 * `AgentProcedure` definition for
 * `.claude/procedures/strip-tool-artifact-tags.sh`. Registered on the
 * docs-sync bundle so it ships alongside `check-links.sh` when the
 * bundle is force-included, and chained into the base PostToolUse
 * Edit|Write hook so authored markdown is cleaned of leaked tool-call
 * artifact tags on write (#779).
 */
export const stripToolArtifactTagsProcedure: AgentProcedure = {
  name: "strip-tool-artifact-tags.sh",
  description:
    "Strips leaked tool-call wrapper closing tags (</content>, </invoke>, </parameter>) that authoring agents occasionally emit as trailing whole lines in markdown, plus any now-trailing blank lines, leaving a single final newline. Operates only on an existing file under docs/src/content/docs/; idempotent, a no-op on clean or missing files, and always exits 0 so it can never fail a PostToolUse hook.",
  content: renderStripToolArtifactTagsProcedure(),
};

/**
 * Render the shell body of the
 * `.claude/procedures/check-doc-samples.sh` helper. Exported so the
 * docs-sync bundle can register it as an `AgentProcedure` and the
 * bundles test suite can assert on the script's contents.
 *
 * The helper wraps the `compileFencedSamples` API exported from
 * `@codedrifters/configulator` (under `src/docs-sync/sample-compilation/`)
 * and emits a single JSON-array stream of failure records on stdout.
 * Detection is **data**, not failure: the helper exits `0` when the
 * compilation phase ran successfully, regardless of how many samples
 * failed to compile. Non-zero exits are reserved for tool-level
 * failures (missing binary, IO error, internal exception).
 *
 * Exit codes: `0` success, `1` usage error or unreadable docs root,
 * `2` a required binary is missing (`node` / `pnpm`), `3` the
 * compilation phase threw an unhandled exception.
 */
export function renderCheckDocSamplesProcedure(): string {
  // Inline Node script that imports `compileFencedSamples` from the
  // workspace `@codedrifters/configulator` package and prints the
  // failure array as JSON. Kept terse so the embedded heredoc stays
  // readable inside the bash render.
  const nodeScript = [
    "(async () => {",
    "  const { compileFencedSamples } = await import('@codedrifters/configulator');",
    "  const docsRoot = process.argv[1];",
    "  const failures = compileFencedSamples({ docsRoot });",
    "  process.stdout.write(JSON.stringify(failures));",
    "})().catch((err) => {",
    "  console.error('check-doc-samples.sh: ' + (err && err.stack ? err.stack : err));",
    "  process.exit(3);",
    "});",
  ].join("\n");
  return [
    "#!/usr/bin/env bash",
    "# check-doc-samples.sh — Compile fenced TS/TSX samples in the Starlight docs.",
    "#",
    "# Usage:",
    "#   .claude/procedures/check-doc-samples.sh <docs-root>",
    "#",
    "# Where:",
    "#   <docs-root> — path to the markdown content tree",
    "#                 (e.g. `docs/src/content/docs`). Walked recursively",
    "#                 for `*.md` files.",
    "#",
    "# Wraps the `compileFencedSamples` API exported from",
    "# `@codedrifters/configulator`. For every fenced ` ```ts `,",
    "# ` ```typescript `, ` ```tsx `, or ` ```typescriptreact ` block in",
    "# the docs, the helper writes the sample to an in-memory file,",
    "# runs the TypeScript compiler against it (in-process, via the TS",
    "# compiler API), and reports any compilation diagnostics.",
    "#",
    "# Skipped samples (fence meta `skip` / `no-check`, or first line",
    "# `// @no-check`) are passed through unchecked.",
    "#",
    "# Output (stdout, single JSON array):",
    "#",
    "#   [",
    "#     {",
    '#       "docPath":     "<path relative to <docs-root>>",',
    '#       "line":        <1-indexed line number of the opening fence>,',
    '#       "fenceIndex":  <0-indexed fenced-block position within the doc>,',
    '#       "lang":        "ts" | "typescript" | "tsx" | "typescriptreact",',
    '#       "diagnostics": [ "<TS<code>: <message>>", ... ]',
    "#     },",
    "#     ...",
    "#   ]",
    "#",
    "# An empty array (`[]`) is emitted when every sample compiled.",
    "# Detection is **data**, not failure: this helper exits 0 when the",
    "# compilation phase ran successfully, regardless of how many",
    "# samples failed to compile. The downstream docs-sync scan phase",
    "# (#520) is responsible for treating failures as one of the two",
    "# hard-block cases per the parent epic.",
    "#",
    "# Exit codes:",
    "#   0  — compilation phase ran; failures (zero or more) are on",
    "#        stdout as a JSON array.",
    "#   1  — usage error (missing args, unreadable docs root).",
    "#   2  — a required binary is not on PATH (`node` / `pnpm`).",
    "#   3  — the compilation phase threw an unhandled exception.",
    "#        Stderr carries the diagnostic.",
    "#",
    "# Dependencies: expects `node` and `pnpm` on PATH and the workspace",
    "# `@codedrifters/configulator` package to be installed (the helper",
    "# resolves the API through `pnpm exec node` from the repo root).",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "check-doc-samples.sh: %s\\n" "$*" >&2',
    "}",
    "",
    'if [ "$#" -lt 1 ]; then',
    '  err "usage: check-doc-samples.sh <docs-root>"',
    "  exit 1",
    "fi",
    "",
    'docs_root="$1"',
    "",
    'if [ ! -d "$docs_root" ]; then',
    '  err "docs root not found: $docs_root"',
    "  exit 1",
    "fi",
    "",
    "if ! command -v node >/dev/null 2>&1; then",
    '  err "node is required but not on PATH"',
    "  exit 2",
    "fi",
    "",
    "if ! command -v pnpm >/dev/null 2>&1; then",
    '  err "pnpm is required but not on PATH"',
    "  exit 2",
    "fi",
    "",
    "# Resolve docs_root to an absolute path so the inline node script",
    "# is independent of the cwd `pnpm exec` chooses.",
    'docs_root_abs="$(cd "$docs_root" && pwd)"',
    "",
    "# Run the inline node script through `pnpm exec` so module",
    "# resolution finds `@codedrifters/configulator` via the workspace",
    "# even when the helper is invoked from a sub-package directory.",
    "# The `--input-type=module` flag lets us use top-level `await` and",
    "# `import()` cleanly. Diagnostics from the script flow to stderr;",
    "# stdout carries only the JSON array.",
    'pnpm exec node --input-type=module -e "' +
      nodeScript.replace(/"/g, '\\"') +
      '" "$docs_root_abs"',
    "status=$?",
    "",
    'if [ "$status" -ne 0 ]; then',
    '  err "compileFencedSamples exited with status $status"',
    "  exit 3",
    "fi",
    "",
    "exit 0",
  ].join("\n");
}

/**
 * `AgentProcedure` definition for
 * `.claude/procedures/check-doc-samples.sh`. Registered on the
 * docs-sync bundle so it ships when the bundle is force-included —
 * matches the packaging of `extractApiProcedure` and
 * `checkLinksProcedure` above. Provides the fenced-sample
 * compilation input the docs-sync scan phase (#520) consumes
 * alongside link integrity, API-extractor, TSDoc-coverage, and
 * doc-reference findings. Per the parent epic, fenced TS samples
 * that fail to compile are one of the two hard-block cases.
 */
export const checkDocSamplesProcedure: AgentProcedure = {
  name: "check-doc-samples.sh",
  description:
    "Fenced-sample compilation checker that wraps the `compileFencedSamples` API and emits a JSON-array stream of { docPath, line, fenceIndex, lang, diagnostics } failure records. Skipped samples (meta `skip`/`no-check`, or first-line `// @no-check`) are passed through unchecked. Detection is data: the helper exits 0 whenever compilation ran successfully regardless of failure count.",
  content: renderCheckDocSamplesProcedure(),
};

/**
 * Docs-sync bundle — scaffolding release.
 *
 * Opt-in via `includeBundles: ["docs-sync"]`. `appliesWhen` returns
 * `false` by default so the scaffold ships disabled until a
 * downstream child issue enables it across the monorepo.
 *
 * Provides the skeleton of a 2-phase drift-detection + audit pipeline
 * (scan → fix) designed for monorepos that keep documentation inside
 * a Starlight singleton. Ships a sub-agent, two user-invocable skills
 * (`/docs-sync-pr`, `/docs-sync-audit`), five new labels
 * (`type:docs-sync`, `docs-sync:scan`, `docs-sync:fix`,
 * `docs-sync:advisory`, `docs-sync:blocking`), and a
 * `Documentation Sync Workflow` rule rendered into CLAUDE.md so
 * humans reading the file see the pipeline exists even while the
 * behavior is still landing across child issues.
 */
export function buildDocsSyncBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
): AgentRuleBundle {
  return {
    name: "docs-sync",
    description:
      "Documentation-sync agent bundle (scaffolding). Two-phase pipeline (scan, fix) with `docs-sync:*` phase labels for detecting code-vs-docs drift introduced by a PR diff or surfaced by a full-repo audit. Ships the skeleton only — drift-detection, mechanical fixes, and audit scoring are carried by downstream child issues of the parent docs-sync epic. Opt-in via `includeBundles: ['docs-sync']`.",
    appliesWhen: () => false,
    rules: [
      {
        name: "docs-sync-workflow",
        description:
          "Describes the 2-phase docs-sync pipeline (scan, fix), the `docs-sync:*` label taxonomy, the mechanical-fix write boundary, and the advisory-vs-blocking routing.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Documentation Sync Workflow",
          "",
          "Use `/docs-sync-pr <branch>` to run a pre-PR drift check, or",
          "`/docs-sync-audit [<scope>]` to run a full-repo audit. The pipeline",
          "runs in 2 phases — scan and fix — each tracked by its own GitHub",
          "issue labeled `docs-sync:scan` or `docs-sync:fix`. All issues carry",
          "`type:docs-sync`.",
          "",
          "Two additional labels classify drift disposition on a PR:",
          "",
          "- `docs-sync:advisory` — drift detected, not blocking the PR.",
          "- `docs-sync:blocking` — drift blocks the PR; applied together",
          "  with `review:human-required` so `pr-reviewer` owns the merge",
          "  gate.",
          "",
          "The docs-sync agent *detects drift and applies mechanical*",
          "*fixes*; it does **not** rewrite conceptual prose, ADRs,",
          "requirement bodies, or BCM capability models. Those go to",
          "`docs:write` follow-ups routed to the appropriate writer",
          "bundle or to a human.",
          "",
          "**Scaffolding release.** This bundle ships the skeleton only.",
          "Drift-detection, mechanical fixes, advisory/blocking routing,",
          "and audit-mode scoring land across downstream child issues of",
          "the parent docs-sync epic. Invoking the skills today produces",
          "an explanatory no-op.",
          "",
          "See the `docs-sync` agent definition for the intended",
          "two-phase contract and the write boundaries.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [buildDocsSyncPrSkill(), buildDocsSyncAuditSkill()],
    subAgents: [buildDocsSyncSubAgent(paths)],
    procedures: [
      extractApiProcedure,
      checkLinksProcedure,
      checkDocSamplesProcedure,
      stripToolArtifactTagsProcedure,
    ],
    labels: [
      {
        name: "type:docs-sync",
        color: "0E8A16",
        description:
          "Work that detects or repairs documentation drift against code (docs-sync pipeline)",
      },
      {
        name: "docs-sync:scan",
        color: "C5DEF5",
        description:
          "Phase 1: scan for drift (API diff, TSDoc coverage, references, links, sample compilation)",
      },
      {
        name: "docs-sync:fix",
        color: "BFDADC",
        description:
          "Phase 2: apply mechanical fixes and file docs:write follow-ups for conceptual changes",
      },
      {
        name: "docs-sync:advisory",
        color: "FBCA04",
        description:
          "Drift detected by docs-sync but does not block the PR; surfaced as a sticky-comment note",
      },
      {
        name: "docs-sync:blocking",
        color: "B60205",
        description:
          "Drift blocks the PR; applied with review:human-required so pr-reviewer owns the merge gate",
      },
    ],
  };
}

/**
 * Default-paths instance of the docs-sync bundle, preserved for
 * parity with other path-aware bundles in this directory. The factory
 * above is the canonical entry point when a consumer supplies
 * `AgentConfigOptions.paths`.
 */
export const docsSyncBundle: AgentRuleBundle = buildDocsSyncBundle();
